'use client';
import {
  useMutation,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { ADMIN_TOKEN_KEY, handleAdmin401 } from '@/lib/admin/session/admin-session';
import { adminKeys } from '@/hooks/api/query-keys';
import {
  GenerateResponseSchema,
  ExchangeResponseSchema,
  SessionsListSchema,
  type SessionsList,
} from '@/lib/admin/zod/impersonation';

// ─── Admin auth helpers ─────────────────────────────────────────────────────────

function getAdminToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

function getAdminHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * A 401 on these routes answers two different questions and they must not be
 * conflated: "who are you?" (token missing/expired/revoked — the session IS
 * over) and "prove it's you" (a step-up credential is required, or the one
 * supplied was wrong — the session is fine and the admin is standing right
 * there).
 *
 * This used to treat EVERY 401 as the first. Impersonation always demands a
 * step-up (`requireReauth` defaults to true), so clicking "Open impersonation"
 * cleared the admin session and redirected to /admin/login — the operator was
 * signed out by the very act of asking them to confirm who they were.
 *
 * `handleAdmin401` is the console's ONE handler (lib/admin/session/admin-session.ts):
 * it exempts every `step_up_*` code, and sends a dead session to the signed-out
 * page. Re-using it — rather than keeping a private copy — is what keeps this
 * file correct the next time a code is added.
 */
async function adminFetch<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  if (typeof window === 'undefined') return undefined as unknown as T;
  try {
    return await fetchApi<T>(endpoint, {
      ...options,
      skipAuth: true,
      headers: {
        ...getAdminHeaders(),
        ...(options?.headers as Record<string, string>),
      },
    });
  } catch (error) {
    handleAdmin401(error);
  }
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Generate an opaque exchange token for impersonating a target user.
 * Requires super-admin JWT (sent via admin Authorization header).
 * Returns { token, expiresAt, redirectUrl }.
 */
export function useGenerateImpersonation() {
  return useMutation({
    mutationFn: async (params: {
      userId: string;
      companyId: string;
      workspaceId: string;
      reason: string;
      /**
       * Step-up credential. `requireReauth` defaults to TRUE, so in practice
       * one of these is always required — the call 401s without it. They were
       * missing here until 2026-09-21, which is why impersonation could not be
       * started at all: the sheet had no field, so the request never carried
       * one, and the refusal was then misread as an expired session.
       *
       * Optional in the type because the policy CAN be switched off, and
       * because an admin whose 2FA was reset for device loss has no code to
       * give — the API decides which credential it will accept.
       */
      twoFactorCode?: string;
      password?: string;
    }) => {
      const raw = await adminFetch<unknown>('/admin/impersonation/generate', {
        method: 'POST',
        body: JSON.stringify(params),
        headers: { 'Content-Type': 'application/json' },
      });
      return GenerateResponseSchema.parse(raw);
    },
  });
}

/**
 * Exchange an opaque token for real user session credentials.
 * PUBLIC endpoint — the token in the body authenticates the request.
 * Called from the exchange page (new browser tab) with no existing session.
 */
export function useExchangeImpersonation() {
  return useMutation({
    mutationFn: async (token: string) => {
      const raw = await fetchApi<unknown>('/admin/impersonation/exchange', {
        method: 'POST',
        body: JSON.stringify({ token }),
        skipAuth: true,
        headers: { 'Content-Type': 'application/json' },
      });
      return ExchangeResponseSchema.parse(raw);
    },
  });
}

/**
 * End the current impersonation session.
 * Called from inside the impersonated session using the USER access token.
 * The backend reads impersonatedBy from the JWT — no extra body needed.
 */
export function useEndImpersonation() {
  return useMutation({
    mutationFn: async () => {
      await fetchApi<unknown>('/admin/impersonation/end', {
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'Content-Type': 'application/json' },
      });
    },
  });
}

/**
 * List impersonation sessions (super-admin audit log).
 * Requires super-admin JWT.
 * Supports paginated filtering via query params.
 */
export function useImpersonationSessions(
  filters: Record<string, unknown> = {},
) {
  return useQuery({
    queryKey: adminKeys.impersonation.sessions(filters),
    queryFn: async (): Promise<SessionsList> => {
      const qs = new URLSearchParams(
        Object.entries(filters).reduce<Record<string, string>>((acc, [k, v]) => {
          if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
          return acc;
        }, {}),
      ).toString();
      const raw = await adminFetch<unknown>(
        `/admin/impersonation/sessions${qs ? `?${qs}` : ''}`,
      );
      return SessionsListSchema.parse(raw);
    },
    placeholderData: keepPreviousData,
  });
}
